import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Download, PhoneCall, PhoneForwarded, Plus, Upload } from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { OverviewToolbar } from "../components/layout/OverviewToolbar";
import { Modal } from "../components/ui/Modal";
import {
  loadViewMode,
  saveViewMode,
  useFitsMultipleColumns,
  ViewToggle,
  type ViewMode,
} from "../components/layout/ViewToggle";
import { agentsApi, type AgentListItem } from "../api/agentsApi";
import { HttpError } from "../api/http";
import { AgentStatusChip } from "../components/AgentStatusChip";
import { DestinationExportModal } from "../components/destinations/DestinationExportModal";
import { DestinationImportModal } from "../components/destinations/DestinationImportModal";

const DESTINATIONS_CARD_MIN = 240;

type LoadState =
  | { status: "loading" }
  | { status: "ready"; items: AgentListItem[] }
  | { status: "error"; message: string };

/**
 * Destinations overview — these records were originally called "agents"
 * in the legacy portal. They are end-points (a phone, an extension, a SIP
 * URI…) that a flow can route a call to. The agentsApi client is reused
 * unchanged because the backend route + payloads still speak "agent".
 */
export function DestinationsPage() {
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [search, setSearch] = useState("");
  const [view, setView] = useState<ViewMode>(() => loadViewMode("destinations"));
  const [modalOpen, setModalOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);
  const canShowToggle = useFitsMultipleColumns(listRef, DESTINATIONS_CARD_MIN);

  const load = useCallback(async () => {
    try {
      const response = await agentsApi.list();
      setState({ status: "ready", items: response.items ?? [] });
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to load destinations";
      setState({ status: "error", message });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (state.status !== "ready") return [] as AgentListItem[];
    const q = search.trim().toLowerCase();
    if (!q) return state.items;
    return state.items.filter(
      (a) =>
        a.agentName.toLowerCase().includes(q) ||
        a.agentRef.toLowerCase().includes(q) ||
        a.destination.toLowerCase().includes(q),
    );
  }, [state, search]);

  const existingNames = useMemo(() => {
    if (state.status !== "ready") return new Set<string>();
    return new Set(
      state.items.map((a) => a.agentName.trim().toLowerCase()),
    );
  }, [state]);

  return (
    <>
      <PageHeader
        title="Destinations"
        actions={
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setExportOpen(true)}
              disabled={state.status !== "ready" || state.items.length === 0}
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <Download size={16} />
              Export
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setImportOpen(true)}
              disabled={state.status !== "ready"}
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <Upload size={16} />
              Import
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setModalOpen(true)}
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <Plus size={16} />
              New destination
            </button>
          </div>
        }
      />
      <div className="dashboard">
        {state.status === "loading" && (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>
            Loading destinations…
          </div>
        )}
        {state.status === "error" && (
          <div
            className="tile"
            style={{
              borderColor: "#e74c3c",
              color: "#c0392b",
              background: "#fdecea",
            }}
          >
            <strong>Something went wrong.</strong>
            <div style={{ marginTop: 6, fontSize: "0.9rem" }}>
              {state.message}
            </div>
          </div>
        )}
        {state.status === "ready" && state.items.length === 0 && <EmptyState />}
        {state.status === "ready" && state.items.length > 0 && (
          <>
            <OverviewToolbar
              search={search}
              onSearch={setSearch}
              placeholder="Search by name or number"
              count={{ matched: filtered.length, total: state.items.length }}
              trailing={
                canShowToggle ? (
                  <ViewToggle
                    value={view}
                    onChange={(next) => {
                      setView(next);
                      saveViewMode("destinations", next);
                    }}
                  />
                ) : null
              }
            />
            <div ref={listRef}>
              {filtered.length === 0 ? (
                <div className="tile" style={{ color: "var(--portal-muted)" }}>
                  No destinations match "{search}".
                </div>
              ) : view === "card" || !canShowToggle ? (
                <DestinationsCardGrid
                  items={filtered}
                  onOpen={(id) => navigate(`/destinations/${id}`)}
                />
              ) : (
                <DestinationsRowList
                  items={filtered}
                  onOpen={(id) => navigate(`/destinations/${id}`)}
                />
              )}
            </div>
          </>
        )}
      </div>

      <CreateDestinationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        existingNames={existingNames}
        onCreated={(id) => {
          setModalOpen(false);
          navigate(`/destinations/${id}`);
        }}
      />
      <DestinationExportModal open={exportOpen} onClose={() => setExportOpen(false)} />
      <DestinationImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        existing={state.status === "ready" ? state.items : []}
        onImported={() => void load()}
      />
    </>
  );
}

function CreateDestinationModal({
  open,
  onClose,
  existingNames,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  existingNames: Set<string>;
  onCreated: (id: number) => void;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state between opens so previously-typed input / errors don't
  // leak into the next invocation.
  useEffect(() => {
    if (!open) {
      setName("");
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const trimmed = name.trim();
  const clientDuplicate =
    trimmed.length > 0 && existingNames.has(trimmed.toLowerCase());
  const canSubmit = !busy && trimmed.length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const created = await agentsApi.create(trimmed);
      onCreated(created.agentId);
    } catch (err) {
      // Prefer the server-side duplicate check (status 409) over the
      // client-side prediction: the list can be stale and the server is
      // the final authority on uniqueness.
      if (err instanceof HttpError && err.status === 409) {
        setError("A destination with that name already exists.");
      } else {
        const fallback =
          err instanceof Error ? err.message : "Failed to create destination";
        const apiDetail =
          err instanceof HttpError && typeof err.data === "object" && err.data
            ? (err.data as { error?: string; detail?: string }).detail ?? null
            : null;
        setError(apiDetail ?? fallback);
      }
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New destination"
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={!canSubmit}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Plus size={16} />
            {busy ? "Creating…" : "Create"}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        style={{ display: "grid", gap: 10 }}
      >
        <label style={{ display: "grid", gap: 4 }}>
          <span
            style={{
              fontSize: "0.8rem",
              color: "var(--portal-muted)",
              fontWeight: 600,
            }}
          >
            Name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            placeholder="e.g. Reception phone"
            maxLength={50}
            autoFocus
            style={{
              padding: "8px 12px",
              border: "1px solid var(--portal-border)",
              borderRadius: 6,
              fontSize: "0.95rem",
              fontFamily: "inherit",
              background: "#ffffff",
            }}
          />
        </label>
        {clientDuplicate && !error && (
          <div style={{ fontSize: "0.85rem", color: "#8a6d00" }}>
            A destination named "{trimmed}" already exists. Pick a different
            name.
          </div>
        )}
        {error && (
          <div style={{ fontSize: "0.85rem", color: "#c0392b" }}>{error}</div>
        )}
        <button type="submit" style={{ display: "none" }} aria-hidden="true" />
      </form>
    </Modal>
  );
}

function EmptyState() {
  return (
    <section
      className="tile"
      style={{
        maxWidth: 720,
        display: "grid",
        gridTemplateColumns: "56px 1fr",
        gap: 18,
        alignItems: "flex-start",
        padding: 24,
      }}
    >
      <div
        className="tile-row-icon"
        style={{ width: 56, height: 56, borderRadius: 14 }}
        aria-hidden="true"
      >
        <PhoneForwarded size={28} />
      </div>
      <div>
        <h3 style={{ marginTop: 0 }}>No destinations yet</h3>
        <p style={{ marginTop: 0 }}>
          Add a destination so calls can be routed to a phone, extension or
          SIP address.
        </p>
      </div>
    </section>
  );
}

function DestinationsCardGrid({
  items,
  onOpen,
}: {
  items: AgentListItem[];
  onOpen: (id: number) => void;
}) {
  return (
    <div
      className="tile-cards"
      style={{ ["--tile-min" as never]: `${DESTINATIONS_CARD_MIN}px` }}
    >
      {items.map((a) => (
        <DestinationCard
          key={a.agentId}
          item={a}
          onOpen={() => onOpen(a.agentId)}
        />
      ))}
    </div>
  );
}

function DestinationCard({
  item,
  onOpen,
}: {
  item: AgentListItem;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      className="tile tile-link"
      onClick={onOpen}
      style={{
        textAlign: "left",
        gap: 10,
        padding: 20,
        background: "white",
      }}
      aria-label={`Open ${item.agentName}`}
    >
      <div
        style={{
          fontWeight: 700,
          fontSize: "1rem",
          color: "var(--portal-text)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={item.agentName}
      >
        {item.agentName || "—"}
      </div>
      <div>
        <AgentStatusChip statusId={item.statusId} statusName={item.statusName} />
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "var(--portal-muted)",
          fontSize: "0.85rem",
          minHeight: 18,
        }}
      >
        {item.pbxEnabled ? (
          <PbxRegistration registered={item.pbxRegistered} />
        ) : (
          <>
            <PhoneCall size={14} />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.destination || "(no number)"}
            </span>
          </>
        )}
      </div>
      <div
        style={{
          marginTop: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          color: "var(--portal-primary)",
          fontWeight: 600,
          fontSize: "0.85rem",
        }}
      >
        Open <ChevronRight size={16} />
      </div>
    </button>
  );
}

/**
 * Replaces the number for PBX destinations: a coloured dot with the
 * registration state, followed by "PBX" so it's clear the endpoint is the
 * SMX PBX login rather than a phone number.
 */
function PbxRegistration({ registered }: { registered: boolean }) {
  const color = registered ? "#1e7e34" : "#c0392b";
  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
      title={registered ? "PBX login is registered" : "PBX login is not registered"}
    >
      <span
        aria-hidden="true"
        style={{ width: 10, height: 10, borderRadius: 999, background: color, flexShrink: 0, boxShadow: `0 0 0 2px ${registered ? "#e6f4ea" : "#fdecea"}` }}
      />
      <span style={{ color }}>{registered ? "Registered" : "Not registered"}</span>
      <span style={{ color: "var(--portal-muted)" }}>PBX</span>
    </span>
  );
}

function DestinationsRowList({
  items,
  onOpen,
}: {
  items: AgentListItem[];
  onOpen: (id: number) => void;
}) {
  return (
    <div className="tile-rows">
      {items.map((a) => (
        <button
          key={a.agentId}
          type="button"
          className="tile tile-link"
          onClick={() => onOpen(a.agentId)}
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 16,
            padding: "12px 18px",
            textAlign: "left",
          }}
          aria-label={`Open ${a.agentName}`}
        >
          <div
            style={{
              flex: "2 1 0",
              minWidth: 0,
              fontWeight: 600,
              fontSize: "1rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={a.agentName}
          >
            {a.agentName || "—"}
          </div>
          <div style={{ flexShrink: 0 }}>
            <AgentStatusChip statusId={a.statusId} statusName={a.statusName} />
          </div>
          <div
            className="tile-row-detail"
            style={{
              flex: "1 1 0",
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: "var(--portal-muted)",
              fontSize: "0.9rem",
              overflow: "hidden",
            }}
          >
            {a.pbxEnabled ? (
              <PbxRegistration registered={a.pbxRegistered} />
            ) : (
              <>
                <PhoneCall size={14} style={{ flexShrink: 0 }} />
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={a.destination}
                >
                  {a.destination || "(no number)"}
                </span>
              </>
            )}
          </div>
          <ChevronRight
            size={16}
            style={{ color: "var(--portal-primary)", flexShrink: 0 }}
          />
        </button>
      ))}
    </div>
  );
}

